import React, { useState } from "react"
import {
  HStack, MenuDivider, MenuItemOption, MenuOptionGroup, Stack, Text,
} from "@chakra-ui/react"
import { useQueryClient } from "react-query"
import * as api from "api/websites"
import { updateWebsite } from "context/actions"
import Button from "components/Buttons/Button"
import CancelButton from "components/Buttons/CancelButton"
import Menu from "components/dialogs/Menu"
import Modal from "components/dialogs/Modal"
import Icon from "components/elements/Icon"
import { chosenTarget } from "components/EventLink/destinations"
import useFeatureFlag from "services/featureFlags"
import { DioboxEvent } from "sharedTypes"
import { successToast } from "utilities/toasts"
import StatusIndicator from "./StatusIndicator"

const STATUSES = [
  { published: false, value: "draft" },
  { published: true, value: "published" },
]

const PublishedUpdater = ({ event }: { event: DioboxEvent }) => {
  const { id: eventId, website } = event
  const queryClient = useQueryClient()
  const { vanityUrls: vanityUrlsEnabled } = useFeatureFlag()
  const [nextPublished, setNextPublished] = useState<boolean | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const eventLinkFallsBack = vanityUrlsEnabled && nextPublished === false && chosenTarget(event) === "site"

  const close = () => {
    if (!isSaving) {
      setNextPublished(null)
    }
  }

  const save = () => {
    if (nextPublished === null) {
      return
    }
    setIsSaving(true)
    api.putWebsite(eventId, { published: nextPublished })
      .then(({ data }) => {
        updateWebsite(queryClient, eventId, data)
        successToast({ title: nextPublished ? "Web Page published" : "Web Page changed to Draft" })
        setNextPublished(null)
      })
      .catch(() => {})
      .finally(() => setIsSaving(false))
  }

  return (
    <>
      <Menu
        title="Publication status"
        placement="bottom-end"
        noPadding
        button={(
          <Button
            as="div"
            variant="outline"
            colorScheme="button.light"
            h={9}
            px={4}
            borderColor="gray.300"
            borderRadius="full"
            fontWeight="semibold"
            color="gray.700"
          >
            <HStack spacing={2}>
              <StatusIndicator published={website.published} url={website.url} dotSize={3} />
              <Icon icon="down-arrow" size={3} color="gray.600" />
            </HStack>
          </Button>
        )}
        menuItems={[
          <MenuOptionGroup
            key="status"
            type="radio"
            value={website.published ? "published" : "draft"}
            onChange={(value) => setNextPublished(value === "published")}
          >
            {STATUSES.flatMap(({ published, value }, index) => [
              ...(index > 0 ? [<MenuDivider key={`divider-${value}`} m={0} />] : []),
              <MenuItemOption key={value} value={value} py={3}>
                <StatusIndicator published={published} url={website.url} dotSize={3} />
              </MenuItemOption>,
            ])}
          </MenuOptionGroup>,
        ]}
      />
      <Modal
        size="lg"
        show={nextPublished !== null}
        hide={close}
        title={nextPublished ? "Publish the Web Page?" : "Change the Web Page to Draft?"}
        actions={[
          <CancelButton key="cancel" onClick={close} isDisabled={isSaving} />,
          <Button key="confirm" onClick={save} isLoading={isSaving}>
            {nextPublished ? "Publish" : "Change to Draft"}
          </Button>,
        ]}
      >
        <Stack spacing={3}>
          <Text>
            {nextPublished
              ? "Anyone with the link will see it."
              : "Guests will no longer see it. Only its preview link opens it."}
            {" "}
            Changes take effect immediately.
          </Text>
          {eventLinkFallsBack && (
            <Text color="orange.500" fontWeight="semibold">
              Until the web page is published again, the Event Link opens the Event Portal.
            </Text>
          )}
        </Stack>
      </Modal>
    </>
  )
}

export default PublishedUpdater
